import type { CSSProperties, FocusEventHandler, JSX, MouseEventHandler, ReactNode } from "react";
import { cloneElement, isValidElement, useEffect, useId, useState } from "react";
import { cx } from "../cx";

interface TooltipTriggerProps {
  "aria-describedby"?: string | undefined;
  onMouseEnter?: MouseEventHandler<Element> | undefined;
  onMouseLeave?: MouseEventHandler<Element> | undefined;
  onFocus?: FocusEventHandler<Element> | undefined;
  onBlur?: FocusEventHandler<Element> | undefined;
}

function chain<Event>(
  theirs: ((event: Event) => void) | undefined,
  ours: () => void,
): (event: Event) => void {
  return (event) => {
    theirs?.(event);
    ours();
  };
}

export interface TooltipProps {
  label: ReactNode;
  side?: "top" | "bottom" | undefined;
  /**
   * A single focusable trigger element. It receives the hover/focus handlers
   * and, while the tooltip is visible, an `aria-describedby` binding.
   */
  children?: ReactNode | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
}

/**
 * Hover/focus tooltip — blue-950 chip, 120ms fade, no arrow. Shows on hover
 * and focus, hides on Escape, blur, and mouse leave. Ported from
 * design/tarski-kit/components/overlay/Tooltip.jsx.
 */
export function Tooltip({
  label,
  side = "top",
  children,
  className,
  style,
}: TooltipProps): JSX.Element {
  const [show, setShow] = useState(false);
  const tooltipId = useId();

  useEffect(() => {
    if (!show) {
      return undefined;
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShow(false);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [show]);

  const trigger = isValidElement<TooltipTriggerProps>(children)
    ? cloneElement(children, {
        "aria-describedby": show ? tooltipId : undefined,
        onMouseEnter: chain(children.props.onMouseEnter, () => setShow(true)),
        onMouseLeave: chain(children.props.onMouseLeave, () => setShow(false)),
        onFocus: chain(children.props.onFocus, () => setShow(true)),
        onBlur: chain(children.props.onBlur, () => setShow(false)),
      })
    : children;

  return (
    <span className={cx("tk-tooltip-anchor", className)} style={style}>
      {trigger}
      {show ? (
        <span role="tooltip" id={tooltipId} className={cx("tk-tooltip", `tk-tooltip--${side}`)}>
          {label}
        </span>
      ) : null}
    </span>
  );
}
